import { List } from 'immutable';
import _ from 'lodash';

import { resolve, resolveAll } from '../helpers/LinkMapper';
import trainTime from '../lib/TrainTime';

import AuthenticatedAPIClient from '../lib/AuthenticatedAPIClient';
import { processImage } from '../helpers/Image';
import { PaymentUnauthorizedError } from '../errors/Payment';
import { ACTError } from '../errors/Smartcard';
import CardLockService from './CardLockService';

import Smartcard from '../records/customer/Smartcard';
import KeyGoTransaction from '../records/customer/KeyGoTransaction';
import SmartcardJourneyRecord from '../records/customer/SmartcardJourneyRecord';
import SmartcardAssociationInfo from '../records/customer/SmartcardAssociationInfo';
import SmartcardAccountInfo from '../records/customer/SmartcardAccountInfo';
import SmartcardAssociationConfirmation from '../records/customer/SmartcardAssociationConfirmation';
import NotificationActions from '../actions/NotificationActions';
import {
    SmartcardPostcodeError,
    SmartcardAlreadyRegisteredError
} from '../errors/Smartcard';


function getCustomerSmartcardUrl (user) {
    return '/customers/:id/smartcard'.replace(/:id\b/, user.id);
}

function getCustomerSmartcardPhotoUrl (user) {
    return '/smartcard/:id/child/photo'.replace(/:id\b/, user.id);
}

function getCustomerLinkSmartcardUrl (user) {
    return '/smartcard/:id/self-registration'.replace(/:id\b/, user.id);
}

function getCustomerSmartcardsUrl (user) {
    return '/customers/:id/smartcards'.replace(/:id\b/, user.id);
}

export function getCustomerSmartcardLinkedAccountsUrl (user) {
    return 'customers/:id/smartcard/linked-accounts'.replace(/:id\b/, decodeURIComponent(user.id));
}

function getCustomerSmartcardActiveLinkedAccountsUrl (user) {
    return 'smartcard/linked-accounts/:id/active'.replace(/:id\b/, decodeURIComponent(user.id));
}

function getCustomerUnlinkSmartcardUrl (user, associatedSmartcard) {
    return '/smartcard/linked-accounts/:id/unlink/:linkId'
        .replace(/:id\b/, decodeURIComponent(user.id))
        .replace(/:linkId\b/, associatedSmartcard.associationId);
    }

function getConfirmSmartcardLinkUrl (customerId, choice, token) {
    return '/smartcard/linked-accounts/:customerId/:choice/:token'
        .replace(/:customerId\b/, customerId)
        .replace(/:choice\b/, choice)
        .replace(/:token\b/, decodeURIComponent(token));

}

function getCustomerKeyGoResumeUrl (user, paymentId) {
    return '/customers/:customerId/keygo/resume/:paymentId'
    .replace(/:customerId\b/, user)
    .replace(/:paymentId\b/, paymentId);
}


const SmartcardService = {

    async getSmartcard (user, enableCache = true) {
        if (user.isLoggedIn() && user.smartcardRegistered) {
            const options = enableCache ? {} : { headers: { 'x-non-cached': 'true' }};

            const response = await AuthenticatedAPIClient.get(getCustomerSmartcardUrl(user), {}, options);
            return resolve(response.result).as(Smartcard);
        }
        return null;
    },

    async getSmartcardPhoto (user) {
        if (user.isLoggedIn() && user.smartcardRegistered) {
            try {
                const response = await AuthenticatedAPIClient.get(getCustomerSmartcardPhotoUrl(user));
                const { typ, value } = response;
                return value ? `data:${typ};base64,${value}` : null;
            } catch (e) {
                return null;
            }
        }
        return null;
    },

    async uploadNewSmartcardPhoto (user, photo) {
        try {
            let processedPhoto = await processImage({ file: photo.selectedImage, maxWidth: 4096, maxHeight: 4096, useBase64: true });
            let typ = photo.selectedImage.type.replace('image/', '').replace('jpeg', 'jpg'); // SE systems don't support jpeg file extension so just use 'jpg'
            await AuthenticatedAPIClient.post(getCustomerSmartcardPhotoUrl(user), { typ, value: processedPhoto});
            return true;
        } catch (err) {
            NotificationActions.showNotification(`account.details.updateErrorNewPhoto`);
            return false;
        }
    },

    async getAllSmartcards (user) {
        if (user.isLoggedIn()) {
            const response = await AuthenticatedAPIClient.get(getCustomerSmartcardsUrl(user));
            return resolveAll(response.result).as(Smartcard);
        }
        return null;
    },

    async registerKeyGo (user, form) {
        return await this.submitKeyGo(user, form);
    },

    async getSmartcardFormData(user, form) {
        const isOver16 = form.isOver16 || form.dateOfBirth.isOverAge(16);

        let files;
        const data = {
            contactName: user.fullName,
            line1: form.addressLine1 || '',
            line2: form.addressLine2 || '',
            line3: form.addressLine3 || '',
            postCode: form.postcode || '',
            town: form.townCity,
            country: form.country,
            autoDrOptedIn: Boolean(form.autoDrOptedIn),
            ...(!isOver16 && { dateOfBirth: form.dateOfBirth.getDateOfBirth().format('YYYY-MM-DD')})
        };

        if (form.requiresPhotograph()) {
            const processedPhoto = await processImage({ file: form.photo.selectedImage, maxWidth: 4096, maxHeight: 4096 });

            files = {
                photo: processedPhoto || form.photo.selectedImage
            };
        }

        return [ data, files ];
    },

    getLinkedSmartcardData(user, form) {
        const isOver16 = form.isOver16 || form.dateOfBirth.isOverAge(16);

        const data = {
            title: user.title,
            forename: user.firstNames,
            surname: user.surname,
            address: {
                line1: form.addressLine1 || '',
                line2: form.addressLine2 || '',
                line3: form.addressLine3 || '',
                postCode: form.postcode || '',
                town: form.townCity,
            },
            email: user.email,
            smartcardNumber: form.isrn.split('-').join(''),
            ...(!isOver16 && { dateOfBirth: form.dateOfBirth.getDateOfBirth().format('YYYY-MM-DD')})
        };
        return data;
    },

    async handleSmartcardErrors(fn) {
        try {
            return await fn();
        } catch (error) {
            const errors = _.result(error, ['body', 'errors'], []);

            if ('SmartcardServiceError' in errors) {
                console.error(error);
                const errorMessage = errors.SmartcardServiceError[0];
                if (errorMessage.indexOf('User already registered') !== -1) {
                    throw new SmartcardAlreadyRegisteredError('User already registered in SEFT/smartcard');
                }
                if (errorMessage.indexOf('locationAddrPostcode=FIELD_IS_BADLY_FORMATTED') !== -1) {
                    throw new SmartcardPostcodeError('Failed postcode check', { error });
                }

            }

            throw error;
        }
    },

    async registerSmartcard (user, form) {
        const [ data, files ] = await this.getSmartcardFormData(user, form);
        const url = getCustomerSmartcardUrl(user);

        return this.handleSmartcardErrors(
            async () => await AuthenticatedAPIClient.postForm(url, data, files)
        );
    },

    linkSmartcard (user, form, type = {}) {
        const { isUpdate = false } = type;
        const data = this.getLinkedSmartcardData(user, form);
        const url = getCustomerLinkSmartcardUrl(user);
        return this.handleSmartcardErrors(
            async () =>
                await AuthenticatedAPIClient[isUpdate ? 'put' : 'post'](url, data)
        );
    },

    async updateKeyGo (user, form) {
        return await this.submitKeyGo(user, form, 'put');
    },

    async keyGoResume (userId, paymentId) {
        return await AuthenticatedAPIClient.get(getCustomerKeyGoResumeUrl(userId, paymentId));
    },

    async submitKeyGo (user, form, method = 'post') {
        const token = await CardLockService.getToken(form.cardNumber, form.cv2);
        const url = getCustomerSmartcardUrl(user) + '/keygo';
        const requestBody = {
            collectionLocationNlc: form.collectionLocation,
            cards: [
                {
                    token,
                    nickname: form.nickname,
                    cardHolderName: form.cardHolderName,
                    expiryDate: form.expiryMonth + '/' + form.expiryYear,
                    email: user.email,
                    phone: '00000000000',
                    address: {
                        line1: form.addressLine1,
                        line2: form.addressLine2,
                        line3: form.addressLine3,
                        town: form.townCity,
                        postCode: form.postcode,
                        country: form.country
                    },
                }
            ]
        };

        try {
            const response = await AuthenticatedAPIClient[method](url, requestBody);

            if (_.result(response, 'status') === 'failed') {
                throw new PaymentUnauthorizedError(response.message);
            }

            return response;
        } catch (error) {
            const { message } = error;
            const errors = _.result(error, ['body', 'errors'], []);

            if (_.findIndex(errors, 'code', ACTError.code) !== -1) {
                throw new ACTError(message);
            } else {
                throw new PaymentUnauthorizedError(message);
            }

        }
    },

    async cancelKeyGo (user) {
        const url = getCustomerSmartcardUrl(user) + '/keygo';
        return await AuthenticatedAPIClient.delete(url);
    },

    getKeyGoTaps (user, smartcard) {
        if (!smartcard.hasKeyGo()) { return Promise.reject(); }

        const url = getCustomerSmartcardUrl(user) + '/keygo/taps';
        const format = 'YYYY-MM-DD';
        const keyGoStart = smartcard.getKeyGoProduct().parameters.StartDate;

        const today = trainTime().startOf('day');
        let start = trainTime().subtract(2, 'months').startOf('day');
        if (start.isBefore(keyGoStart)) {
            start = keyGoStart.clone();
        }

        let requests = [];

        while (start.isBefore(today)) {
            let end = start.clone().add(1, 'month');
            if (end.isAfter(today)) { end = today.clone(); }

            const startDate = start.format(format);
            const endDate = end.format(format);
            requests.push(AuthenticatedAPIClient.get(url, { startDate, endDate }));

            start = end.clone();
        }

        return Promise.all(requests).then((responses) => {
            const taps = responses.map((response) => resolveAll(response.transactions).as(KeyGoTransaction));
            return new List().concat(...taps).sort(function(a, b){
                // Turn your strings into dates, and then subtract them
                // to get a value that is either negative, positive, or zero.
                return new Date(b.dateTime) - new Date(a.dateTime);
            });
        });
    },

    async getJourneys (user, smartcard) {
        if (smartcard.hasKeyGo()) { return Promise.reject(); }

        const url = getCustomerSmartcardUrl(user) + '/journeys';
        const response = await AuthenticatedAPIClient.get(url);
        return resolveAll(response).as(SmartcardJourneyRecord).filter((record) => record.isJourney());
    },

    async fetchChildSmartcardReplacement (user) {
        const url = `/smartcard/${user.id}/preferences`;
        return await AuthenticatedAPIClient.get(url);
    },

    async requestChildSmartcardReplacement ({ user, isCardReplacementRequested }) {
        const url = `/smartcard/${user.id}/preferences`;
        return await AuthenticatedAPIClient.put(url, { isCardReplacementRequested });
    },

    async replaceSmartcard (user, { reason, additionalDetails, crimeNumber }) {
        const url = getCustomerSmartcardUrl(user) + '/replacement';
        return await AuthenticatedAPIClient.post(url, { reason, additionalDetails, crimeNumber });
    },

    async getActiveAssociatedSmartcards(user) {
        const url = getCustomerSmartcardActiveLinkedAccountsUrl(user);

        try {
            const result = await AuthenticatedAPIClient.get(url);
            return result;
        } catch (error) {
            console.warn(error);
            throw error;
        }
    },

    async getAssociatedSmartcards(user, onlyActiveSmartcards) {
        let result;

        try {
            const url = getCustomerSmartcardLinkedAccountsUrl(user) + '/smartcards';
            const response = await AuthenticatedAPIClient.get(url);

            if (onlyActiveSmartcards) {
                const activeSmartcards = await this.getActiveAssociatedSmartcards(user);
                result = response.result.reduce((acc, smartcard) => {
                    const activeSmartcard = activeSmartcards.find(({ isrn }) => isrn === smartcard.linkedSmartcardNumber);
                    if (activeSmartcard) {
                        acc.push({
                            ...smartcard,
                            expiryDate: activeSmartcard.expiryDate,
                            cardHolderDateOfBirth: activeSmartcard.cardHolderDateOfBirth,
                        });
                    }
                    return acc;
                }, []);
            } else {
                result = response.result;
            }
        } catch (error) {
            console.warn(error);
            throw error;
        }

        return resolveAll(result).as(SmartcardAssociationInfo);
    },

    async getAssociatedAccounts(user) {
        const url = getCustomerSmartcardLinkedAccountsUrl(user) + '/accounts';
        let response;

        try {
            response = await AuthenticatedAPIClient.get(url);
        } catch (error) {
            console.warn(error);
            throw error;
        }

        return resolveAll(response.result).as(SmartcardAssociationInfo);
    },

    async queryAvailableAssociations(user, query) {
        const url = getCustomerSmartcardLinkedAccountsUrl(user) + '/search';
        const response = await AuthenticatedAPIClient.get(url, query);
        return resolve(response.result).as(SmartcardAccountInfo);
    },

    async associateSmartcard(user, smartcardNumber) {
        const url = getCustomerSmartcardLinkedAccountsUrl(user) + '/link';
        return await AuthenticatedAPIClient.post(url, { smartcardNumber });
    },

    async dissociateSmartcard(user, associatedSmartcard) {
        const url = getCustomerUnlinkSmartcardUrl(user, associatedSmartcard);
        return await AuthenticatedAPIClient.put(url);
    },

    async dissociateAccount(user, associatedSmartcard) {
        const url = getCustomerUnlinkSmartcardUrl(user, associatedSmartcard);
        return await AuthenticatedAPIClient.put(url);
    },

    async confirmAssociation(customerId, choice, token) {
        const url = getConfirmSmartcardLinkUrl(customerId, choice, token);
        const response = await AuthenticatedAPIClient.put(url);

        return resolve(response).as(SmartcardAssociationConfirmation);
    }

};


export default SmartcardService;



// WEBPACK FOOTER //
// ./src/services/SmartcardService.js